<template>
  <div>
    <poppins-text-field
      id="first-name"
      v-model="form.firstName"
      label-name="First name"
      aria-label="First Name"
      name="firstName"
      type="text"
      class="mb-16"
      autocomplete="given-name"
      :status="formStatuses.firstName"
      :helper-text="helperText(formStatuses.firstName, 'firstName')"
      @blur="validateName('firstName')"
    />
    <poppins-text-field
      id="last-name"
      v-model="form.lastName"
      label-name="Last name"
      aria-label="Last Name"
      name="lastName"
      type="text"
      class="mb-16"
      autocomplete="family-name"
      :status="formStatuses.lastName"
      :helper-text="helperText(formStatuses.lastName, 'lastName')"
      @blur="validateName('lastName')"
    />
    <poppins-email-text-field
      id="email"
      ref="emailTextField"
      v-model="form.email"
      label-name="Email"
      name="email"
      class="mb-16"
      :status="formStatuses.email"
      :helper-text="helperText(formStatuses.email, 'email')"
      @update:status="updateStatus($event, 'email')"
    />
    <poppins-password-text-field
      id="password"
      ref="passwordTextField"
      v-model="form.password"
      label-name="Password"
      name="password"
      class="mb-16"
      :status="formStatuses.password"
      autocomplete="new-password"
      @blur="validatePassword"
    />
    <poppins-text-field
      id="zipcode"
      v-model="form.zipcode"
      label-name="ZIP code"
      name="postal-code"
      type="text"
      maxlength="5"
      pattern="[0-9]*"
      class="mb-24"
      inputmode="numeric"
      aria-label="Five-digit zip code"
      :status="formStatuses.zipcode"
      :helper-text="helperText(formStatuses.zipcode, 'zipcode', form.zipcode)"
      :limit-input-to-pattern="true"
      @blur="validateZipcode"
    />
  </div>
</template>

<script lang="ts">
import startCase from 'lodash/startCase';
import helperStatus from '../../helpers/status';
import { isValidZipcode } from '@/lib/geocode';
import { isValidName, INVALID_NAME_MESSAGE } from '@/helpers/nameHelper';

import TextField from '../textfield/TextField.vue';
import PasswordTextField from '../passwordTextField/PasswordTextField.vue';
import EmailTextField from '../email_text_field/EmailTextField.vue';

export default {
  name: 'PoppinsAccountCreateFields',
  components: {
    PoppinsTextField: TextField,
    PoppinsPasswordTextField: PasswordTextField,
    PoppinsEmailTextField: EmailTextField,
  },
  props: {
    requiredFields: {
      type: Array,
      default: () => [],
    },
    serverError: {
      type: Error,
      default: null,
    },
  },
  data(): any {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        zipcode: '',
      },
      formStatuses: {
        firstName: helperStatus.Neutral,
        lastName: helperStatus.Neutral,
        email: helperStatus.Neutral,
        password: helperStatus.Neutral,
        zipcode: helperStatus.Neutral,
      },
    };
  },
  computed: {
    formStatusesErrors(): Array<string> {
      return Object.keys(this.formStatuses).filter((key) => this.formStatuses[key] === helperStatus.Error);
    },
  },
  watch: {
    // error state must get reset when user starts typing
    'form.firstName': function () {
      this.onInput();
      this.resetErrorState('firstName');
    },
    'form.lastName': function () {
      this.onInput();
      this.resetErrorState('lastName');
    },
    'form.email': function () {
      this.onInput();
      if (this.serverError?.message === 'email-not-unique' || this.serverError?.message === 'email-invalid') {
        this.$emit('resetServerError');
      }
    },
    'form.password': function () {
      this.onInput();
      this.resetErrorState('password');
    },
    'form.zipcode': function (newValue: string) {
      if (isValidZipcode(newValue)) {
        // emit zipcode only after validation because geolocation will break if we don't
        this.onInput();
        this.resetErrorState('zipcode');
      }
    },
    serverError(newValue) {
      if (newValue?.message === 'email-not-unique' || newValue?.message === 'email-invalid') {
        this.triggerErrorState('email');
      }
    },
  },
  methods: {
    onInput() {
      this.$emit('update:modelValue', this.form);
    },
    // helper methods for validation
    triggerErrorState(fieldName) {
      this.formStatuses[fieldName] = helperStatus.Error;
    },
    resetErrorState(fieldName) {
      this.formStatuses[fieldName] = helperStatus.Neutral;
    },
    triggerWarnState(fieldName) {
      this.formStatuses[fieldName] = helperStatus.Warn;
    },
    resetWarnState(fieldName) {
      this.formStatuses[fieldName] = helperStatus.Neutral;
    },
    fieldIsEmpty(fieldValue) {
      return fieldValue.length === 0;
    },
    fieldIsRequired(fieldName) {
      return this.requiredFields.includes(fieldName);
    },
    updateStatus(newStatus, fieldName) {
      this.formStatuses[fieldName] = newStatus;
    },
    // validation is triggered on blur or submit button clicked in parent
    validateAll() {
      Object.entries(this.form).forEach((e) => {
        this.validateRequiredFields(e[0], e[1]);
      });
      this.validateName('firstName');
      this.validateName('lastName');
      this.validateEmailLocal();
      this.validatePassword();
      this.validateZipcode();

      return this.formStatusesErrors.length === 0;
    },
    validateRequiredFields(fieldName, fieldValue) {
      if (this.fieldIsRequired(fieldName) && this.fieldIsEmpty(fieldValue)) {
        this.triggerErrorState(fieldName);
      }
    },
    validateName(fieldName) {
      const value = this.form[fieldName] ?? '';
      if (this.fieldIsRequired(fieldName) && this.fieldIsEmpty(value)) {
        this.triggerErrorState(fieldName);
        return;
      }
      if (!this.fieldIsEmpty(value) && !isValidName(value)) {
        this.triggerErrorState(fieldName);
      }
    },
    validateEmailLocal() {
      this.$refs.emailTextField.validateEmail();
    },
    validatePassword() {
      if (!this.$refs.passwordTextField.allRequirementsMet) {
        this.triggerErrorState('password');
      }
    },
    validateZipcode() {
      const zip = this.form.zipcode;
      if (this.fieldIsRequired('zipcode') && this.fieldIsEmpty(zip)) {
        this.triggerErrorState('zipcode');
        return;
      }
      if (!isValidZipcode(zip)) {
        this.triggerErrorState('zipcode');
      }
    },
    helperText(status: helperStatus, type: string, fieldValue?: string): string {
      if (status !== helperStatus.Error && status !== helperStatus.Warn) return '';

      if (type === 'zipcode') {
        const z = fieldValue ?? this.form.zipcode;
        if (!z || this.fieldIsEmpty(z)) {
          return 'ZIP code is required.';
        }
        return 'Enter a valid U.S. ZIP code.';
      }

      if (type === 'email') {
        if (this.serverError?.message === 'email-not-unique') {
          return 'This email is already associated with an account';
        }
        if (this.serverError?.message === 'email-invalid') {
          return 'Please enter a valid email address';
        }
      } else if ((type === 'firstName' || type === 'lastName') && !this.fieldIsEmpty(this.form[type] ?? '')) {
        return INVALID_NAME_MESSAGE;
      } else {
        return `${startCase(type)} is required`;
      }

      return '';
    },
  },
};
</script>
